import React from 'react';
import { __, sprintf } from '@wordpress/i18n';

// helpers
import { getInstrumentLabel, getUserFullName } from '../../../../helpers/recSession';

// components
import UserAvatar from '../../../../components/UserAvatar';
import CaretRightIcon from '../../../../icons/CaretRightIcon';
import CaretLeftIcon from '../../../../icons/CaretLeftIcon';
import VolumeIcon from '../../../../icons/VolumeIcon';

const VOLUME_STEP = 10;

const clampVolume = (value) => Math.max(0, Math.min(100, Math.round(value)));

/**
 * The label next to a collaborator's lane — Figma "floating-label-RecSession"
 * (6072:25528). Collapsed ("close") it is the avatar and a chevron; clicking
 * it opens the "Default" card with mute / solo, the name and the lane volume.
 */
const RecLaneLabel = ({
    layer,
    expanded = false,
    onToggle,
    muted = false,
    soloed = false,
    onMute,
    onSolo,
    volume = 100,
    onVolumeChange,
    onVolumeCommit,
}) => {
    const user = layer.assignedUser;
    const name = getUserFullName(user) || getInstrumentLabel(layer.sessionType) || __('Traccia', 'soundbetween');
    const instrument = getInstrumentLabel(layer.sessionType);
    const title = [getUserFullName(user), instrument].filter(Boolean).join(' · ');
    const avatarUrl = user?.profilePictureUrl ?? null;

    if (!expanded) {
        return (
            <button
                type="button"
                className={`recLaneLabel recLaneLabel--collapsed${muted ? ' recLaneLabel--muted' : ''}`}
                onClick={onToggle}
                title={title}
                aria-label={sprintf(__('Apri controlli di %s', 'soundbetween'), name)}
                aria-expanded={false}
            >
                <UserAvatar url={avatarUrl} name={name}/>
                <CaretRightIcon/>
            </button>
        );
    }

    const setVolume = (value) => onVolumeChange?.(clampVolume(value));
    const stepVolume = (delta) => {
        const next = clampVolume(volume + delta);
        onVolumeChange?.(next);
        onVolumeCommit?.(next);
    };

    return (
        <div className="recLaneLabel recLaneLabel--expanded" role="group" aria-label={title}>
            <div className="recLaneLabel__ms">
                <button
                    type="button"
                    className={`recLaneLabel__msBtn${muted ? ' recLaneLabel__msBtn--on' : ''}`}
                    onClick={onMute}
                    aria-pressed={muted}
                    title={__('Muto', 'soundbetween')}
                >
                    M
                </button>
                <button
                    type="button"
                    className={`recLaneLabel__msBtn${soloed ? ' recLaneLabel__msBtn--on' : ''}`}
                    onClick={onSolo}
                    aria-pressed={soloed}
                    title={__('Solo', 'soundbetween')}
                >
                    S
                </button>
            </div>

            <div className="recLaneLabel__body">
                <div className="recLaneLabel__who" title={title}>
                    <UserAvatar url={avatarUrl} name={name}/>
                    <span className="recLaneLabel__name">{name}</span>
                </div>
                <div className="recLaneLabel__volume">
                    <VolumeIcon/>
                    <button
                        type="button"
                        className="recLaneLabel__step"
                        onClick={() => stepVolume(-VOLUME_STEP)}
                        aria-label={__('Abbassa volume', 'soundbetween')}
                    >
                        −
                    </button>
                    <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={volume}
                        aria-label={sprintf(__('Volume di %s', 'soundbetween'), name)}
                        onChange={e => setVolume(Number(e.target.value))}
                        onPointerUp={e => onVolumeCommit?.(clampVolume(Number(e.currentTarget.value)))}
                        onKeyUp={e => onVolumeCommit?.(clampVolume(Number(e.currentTarget.value)))}
                    />
                    <button
                        type="button"
                        className="recLaneLabel__step"
                        onClick={() => stepVolume(VOLUME_STEP)}
                        aria-label={__('Alza volume', 'soundbetween')}
                    >
                        +
                    </button>
                </div>
            </div>

            <button
                type="button"
                className="recLaneLabel__collapse"
                onClick={onToggle}
                aria-label={__('Chiudi controlli', 'soundbetween')}
                aria-expanded={true}
            >
                <CaretLeftIcon/>
            </button>
        </div>
    );
};

export default RecLaneLabel;
